import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import PageIntro from 'components/PageIntro/PageIntro';
import WithLoader from 'components/WithLoader';
import { NotificationCenterContext } from 'components/NotificationCenter';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { commonActions } from 'app/store/common.slice';
import { axiosInstance } from 'setup/axiosInstance';
import { DictionaryWord, LearningSession } from 'app/types';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { CEFR_META } from 'components/cefrMeta';
import DictionaryFilterBar from 'components/dictionaryView/DictionaryFilterBar';
import DictionaryLegend from 'components/dictionaryView/DictionaryLegend';
import DictionaryWordsView from 'components/dictionaryView/DictionaryWordsView';
import { useWordsView } from 'components/dictionaryView/useWordsView';
import TableSettingsDialog from 'components/TableSettingsDialog';
import {
  createDefaultColumnSettings,
  tableSettingsToColumnsVisibility,
} from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';

type VocabularyWordStatus = 'learned' | 'notLearned' | 'repeat';

const statusOptions: {
  label: string;
  value: VocabularyWordStatus;
  isActiveSpeech: boolean;
  isLearned: boolean;
}[] = [
  {
    label: 'Не вивчено',
    value: 'notLearned',
    isLearned: false,
    isActiveSpeech: false,
  },
  {
    label: 'Вивчено',
    value: 'learned',
    isLearned: true,
    isActiveSpeech: false,
  },
  {
    label: 'Потребує повторення',
    value: 'repeat',
    isLearned: true,
    isActiveSpeech: false,
  },
];

const DEFAULT_DICTIONARY_WORD_COLUMNS = createDefaultColumnSettings({
  definition: true,
  number: true,
  partOfSpeech: true,
  transcription: true,
  translation: true,
  word: true,
});

const SubCategoryOverview: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    categoryName = '',
    subcategoryName = '',
    wordSetType = '',
  } = useParams();
  const { pushNotification } = useContext(NotificationCenterContext);

  const { data, loading } = useAppSelector(
    (store) => store.common.subCategoryWords,
  );

  const [adding, setAdding] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [selectedStatus, setSelectedStatus] =
    useState<VocabularyWordStatus>('notLearned');
  const [learningStartLoading, setLearningStartLoading] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );

  const tableSettingsKey =
    data[0]?.wordType === 'EXPRESSION'
      ? 'DICTIONARY_EXPRESSIONS'
      : 'DICTIONARY_WORDS';
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings(tableSettingsKey, DEFAULT_DICTIONARY_WORD_COLUMNS);
  const tableColumns = useMemo(
    () => tableSettingsToColumnsVisibility(tableSettings, { select: true }),
    [tableSettings],
  );

  const view = useWordsView(data as DictionaryWord[], selectionResetKey);

  const selectedIds = useMemo(
    () =>
      Object.entries(view.selected)
        .filter(([, v]) => v)
        .map(([id]) => Number(id)),
    [view.selected],
  );

  useEffect(() => {
    if (categoryName && subcategoryName) {
      dispatch(
        commonActions.getSubCategoryWords({
          wordSetType,
          categoryName,
          subcategoryName,
        }),
      );
    }
  }, [categoryName, dispatch, subcategoryName, wordSetType]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const openAddDialog = () => {
    if (!selectedIds.length) return;
    setSelectedStatus('notLearned');
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => setAddDialogOpen(false);

  const addWords = async () => {
    const selectedOption = statusOptions.find(
      ({ value }) => value === selectedStatus,
    );
    if (!selectedOption || !selectedIds.length) return;

    try {
      setAdding(true);
      const selectedWords = data.filter(({ id }) =>
        selectedIds.includes(id),
      ) as DictionaryWord[];
      const wordType = selectedWords[0]?.wordType || 'WORD';
      const endpoint =
        wordType === 'EXPRESSION'
          ? `/vocabulary/expressions/`
          : `/vocabulary/words`;

      // Stamp the dictionary-bulk origin so the "Нещодавні" grouping on the
      // vocabulary page later reads "<DD.MM.YYYY> · A1 · People · N слів"
      // instead of a useless month bucket.
      const sourceLabel = [categoryName, subcategoryName]
        .filter(Boolean)
        .join(' · ');
      await axiosInstance.post(
        endpoint,
        selectedWords.map((record) => ({
          ...record,
          isLearned: selectedOption.isLearned,
          isActiveSpeech: selectedOption.isActiveSpeech,
        })),
        {
          params: {
            wordType: wordType === 'WORD' ? wordType : undefined,
            source: 'DICTIONARY_BULK',
            sourceLabel: sourceLabel || undefined,
          },
        },
      );
      setAddDialogOpen(false);
      setSelectionResetKey((prev) => prev + 1);
      dispatch(
        commonActions.getSubCategoryWords({
          wordSetType,
          categoryName,
          subcategoryName,
        }),
      );
      pushNotification(
        `Додано ${selectedIds.length} слів в словник`,
        'success',
        undefined,
        'center',
      );
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  };

  const startLearning = async () => {
    if (!selectedIds.length) return;

    setLearningStartLoading(true);
    try {
      const { data: learningSession } =
        await axiosInstance.post<LearningSession>('/learning-sessions', {
          mode: 'MANUAL',
          source: 'DICTIONARY',
          wordIds: selectedIds,
        });
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(learningSession.id),
        }),
      );
    } finally {
      setLearningStartLoading(false);
    }
  };

  const total = data.length;
  const inVocabCount = useMemo(
    () => (data as DictionaryWord[]).filter((w) => w.isLearned).length,
    [data],
  );

  const cefr = CEFR_META[categoryName];
  const headerTitle = subcategoryName || categoryName;
  const levelCode = cefr ? categoryName : undefined;
  const levelName = cefr?.en;
  const newCount = Math.max(total - inVocabCount, 0);
  const pct = total ? Math.round((inVocabCount / total) * 100) : 0;
  const fmt = (n: number) => n.toLocaleString('uk-UA');
  const suffix = selectedIds.length ? ` · ${selectedIds.length}` : '';
  const actionsDisabled = !selectedIds.length;

  const pageActions = (
    <>
      <FilledActionButton
        disabled={actionsDisabled || learningStartLoading}
        onClick={startLearning}
        startIcon={<AutoStoriesOutlinedIcon sx={{ fontSize: 17 }} />}
      >
        {`Почати вправи${suffix}`}
      </FilledActionButton>
      <FilledActionButton
        disabled={actionsDisabled}
        loading={adding}
        onClick={openAddDialog}
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
      >
        {`Додати в словник${suffix}`}
      </FilledActionButton>
    </>
  );

  const levelChip = levelCode ? (
    <Box
      sx={{
        bgcolor: (t) =>
          t.palette.mode === 'light'
            ? 'rgba(59,44,147,0.10)'
            : 'rgba(155,124,255,0.18)',
        borderRadius: 999,
        color: 'primary.main',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.04em',
        px: 1.375,
        py: '4px',
      }}
    >
      {levelName ? `${levelCode} · ${levelName}` : levelCode}
    </Box>
  ) : undefined;

  const introDescription = (
    <>
      {`${fmt(total)} слів · `}
      <Box component={'b'} sx={{ color: 'text.primary', fontWeight: 700 }}>
        {fmt(inVocabCount)}
      </Box>
      {' у твоєму словнику · '}
      <Box component={'b'} sx={{ color: 'primary.main', fontWeight: 700 }}>
        {fmt(newCount)}
      </Box>
      {' нових'}
    </>
  );

  const progressFooter = (
    <Stack direction={'row'} alignItems={'center'} spacing={1.75}>
      <Box
        sx={(t) => ({
          bgcolor:
            t.palette.mode === 'light' ? 'grey.200' : 'rgba(255,255,255,0.08)',
          borderRadius: 999,
          flex: 'none',
          height: 8,
          maxWidth: '60vw',
          overflow: 'hidden',
          width: 220,
        })}
      >
        <Box
          sx={(t) => ({
            bgcolor: t.palette.mode === 'light' ? '#3B8E2F' : '#5FC74C',
            height: '100%',
            transition: 'width .25s ease',
            width: `${pct}%`,
          })}
        />
      </Box>
      <Typography
        sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 700 }}
      >
        {`${pct}% у словнику`}
      </Typography>
    </Stack>
  );

  return (
    <>
      <UIContainer>
        <BreadCrumbs actions={pageActions} />
      </UIContainer>

      <WithLoader loading={loading}>
        <UIContainer sx={{ pb: 6 }}>
          <PageIntro
            title={headerTitle}
            description={introDescription}
            eyebrow={levelChip}
            footer={progressFooter}
          />

          <DictionaryFilterBar
            view={view}
            onOpenSettings={(anchor) => setSettingsAnchor(anchor)}
            settingsOpen={!!settingsAnchor}
          />
          <DictionaryLegend />

          <DictionaryWordsView
            view={view}
            columns={tableColumns}
          />

          <Stack sx={{ minHeight: 40 }} />
        </UIContainer>
      </WithLoader>

      <Dialog
        disableScrollLock
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth={'xs'}
      >
        <DialogTitle
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {`Виберіть стан для ${selectedIds.length} обраних слів:`}
          <IconButton onClick={closeAddDialog} size={'small'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {statusOptions.map(({ label, value }) => (
            <Box
              key={value}
              onClick={() => setSelectedStatus(value)}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
              }}
            >
              {label}
              <Checkbox
                checked={selectedStatus === value}
                disableTouchRipple
                onChange={() => setSelectedStatus(value)}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>{'Скасувати'}</Button>
          <Button
            loading={adding}
            onClick={addWords}
            variant={'contained'}
          >
            {'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <TableSettingsDialog
        anchorEl={settingsAnchor}
        settings={tableSettings}
        viewMode={view.viewMode}
        cards={{
          cardsPerRow: view.cardsPerRow,
          setCardsPerRow: view.setCardsPerRow,
        }}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetTableSettings}
        onSave={saveTableSettings}
      />
    </>
  );
};

export default SubCategoryOverview;
